import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import VM from 'openblock-vm';

import Box from '../components/box/box.jsx';
import Modal from './modal.jsx';
import {closeFileMenu} from '../reducers/menus';
import {
    LoadingStates,
    LoadingState,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';
import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';
import MessageBoxType from '../lib/message-box.js';
import {
    base64ToArrayBuffer,
    fetchLabcodeStudentProject,
    listLabcodeStudentProjects
} from '../lib/labcode-api';
import {getLabcodeStudentProfile, setLabcodeCloudProjectId} from '../lib/labcode-student-session';

import styles from './labcode-my-projects-modal.css';

const messages = defineMessages({
    replaceCloud: {
        id: 'gui.labcode.replaceProjectCloud',
        defaultMessage: 'Substituir o projeto atual pelo projeto selecionado? ' +
            'Alterações não guardadas serão perdidas.',
        description: 'Confirm before loading a cloud project over local changes'
    }
});

class LabcodeProjectRow extends React.PureComponent {
    constructor (props) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
    }
    handleClick () {
        this.props.onSelect(this.props.row);
    }
    render () {
        const {loadingId, row} = this.props;
        const busy = loadingId === row.id;
        return (
            <li
                className={styles.row}
            >
                <div className={styles.rowText}>
                    <span className={styles.title}>
                        {row.title || 'Sem título'}
                    </span>
                    <span className={styles.date}>
                        {row.updatedAt ?
                            new Date(row.updatedAt).toLocaleString() :
                            ''}
                    </span>
                </div>
                <button
                    className={styles.loadBtn}
                    disabled={Boolean(loadingId)}
                    type="button"
                    onClick={this.handleClick}
                >
                    {busy ? 'A abrir…' : 'Carregar'}
                </button>
            </li>
        );
    }
}

LabcodeProjectRow.propTypes = {
    loadingId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    row: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        updatedAt: PropTypes.string
    }).isRequired
};

LabcodeProjectRow.defaultProps = {
    loadingId: null
};

/**
 * Lista e carrega projetos guardados na API (sessão de aluno).
 * @param {object} props — propriedades React.
 * @returns {React.ReactElement|null} Modal ou nada.
 */
const LabcodeMyProjectsModal = function (props) {
    const {
        intl,
        isOpen,
        loadingState,
        onRequestClose,
        onShowMessageBox,
        projectChanged,
        vm,
        closeFileMenuDispatch,
        openLoadingProjectDispatch,
        onLoadingFinishedDispatch,
        setProjectTitleDispatch,
        requestProjectUploadDispatch
    } = props;

    const [list, setList] = useState([]);
    const [listError, setListError] = useState(null);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingId, setLoadingId] = useState(null);

    const refreshList = useCallback(async () => {
        setListError(null);
        setLoadingList(true);
        try {
            const data = await listLabcodeStudentProjects();
            setList(data.projects || []);
        } catch (e) {
            setList([]);
            setListError(e && e.message ? e.message : 'Erro ao listar projetos.');
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            refreshList();
        }
    }, [isOpen, refreshList]);

    const handleClose = useCallback(() => {
        setListError(null);
        onRequestClose();
    }, [onRequestClose]);

    const loadOne = useCallback(async row => {
        let uploadAllowed = true;
        if (projectChanged) {
            uploadAllowed = onShowMessageBox(
                MessageBoxType.confirm,
                intl.formatMessage(messages.replaceCloud)
            );
        }
        if (!uploadAllowed) return;

        closeFileMenuDispatch();
        requestProjectUploadDispatch(loadingState);
        setLoadingId(row.id);
        openLoadingProjectDispatch();

        let ok = false;
        try {
            const data = await fetchLabcodeStudentProject(row.id);
            const project = data.project;
            const b64 =
                project &&
                project.blockData &&
                project.blockData.sb3Base64;
            if (!b64 || typeof b64 !== 'string') {
                throw new Error('Formato de projeto inválido ou dados em falta.');
            }
            const buf = base64ToArrayBuffer(b64);
            await vm.loadProject(buf);
            const title =
                project.title && String(project.title).trim().length ?
                    String(project.title).trim() :
                    'Projeto';
            setProjectTitleDispatch(title);
            setLabcodeCloudProjectId(project.id);
            ok = true;
        } catch (e) {
            onShowMessageBox(
                MessageBoxType.alert,
                e && e.message ? e.message : 'Não foi possível carregar o projeto.'
            );
        } finally {
            onLoadingFinishedDispatch(LoadingState.LOADING_VM_FILE_UPLOAD, ok);
            setLoadingId(null);
            handleClose();
        }
    }, [
        projectChanged,
        onShowMessageBox,
        intl,
        closeFileMenuDispatch,
        requestProjectUploadDispatch,
        loadingState,
        openLoadingProjectDispatch,
        vm,
        setProjectTitleDispatch,
        onLoadingFinishedDispatch,
        handleClose
    ]);

    if (!isOpen) {
        return null;
    }

    const profile = getLabcodeStudentProfile();
    let sub = '';
    if (profile && profile.nickname && profile.classCode) {
        sub = `${profile.nickname} · ${profile.classCode}`;
    } else if (profile && profile.nickname) {
        sub = profile.nickname;
    }

    return (
        <Modal
            className={styles.modalShell}
            closeButtonVisible
            contentLabel="Meus projetos na turma"
            headerClassName={styles.modalHeader}
            id="labcodeMyProjectsModal"
            onRequestClose={handleClose}
        >
            <Box
                className={styles.body}
                direction="column"
            >
                {sub ? (
                    <p className={styles.meta}>
                        {sub}
                    </p>
                ) : null}
                {listError ? (
                    <p
                        className={styles.error}
                        role="alert"
                    >
                        {listError}
                    </p>
                ) : null}
                {loadingList ? (
                    <p className={styles.muted}>
                        {'A carregar…'}
                    </p>
                ) : null}
                {!loadingList && list.length === 0 && !listError ? (
                    <p className={styles.muted}>
                        {
                            'Ainda não há projetos guardados. Use Ficheiro → Guardar projeto na turma.'
                        }
                    </p>
                ) : null}
                <ul className={styles.list}>
                    {list.map(row => (
                        <LabcodeProjectRow
                            key={row.id}
                            loadingId={loadingId}
                            row={row}
                            onSelect={loadOne}
                        />
                    ))}
                </ul>
            </Box>
        </Modal>
    );
};

LabcodeMyProjectsModal.propTypes = {
    closeFileMenuDispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    isOpen: PropTypes.bool,
    loadingState: PropTypes.oneOf(LoadingStates).isRequired,
    onLoadingFinishedDispatch: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onShowMessageBox: PropTypes.func.isRequired,
    openLoadingProjectDispatch: PropTypes.func.isRequired,
    projectChanged: PropTypes.bool,
    requestProjectUploadDispatch: PropTypes.func.isRequired,
    setProjectTitleDispatch: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

LabcodeMyProjectsModal.defaultProps = {
    isOpen: false,
    projectChanged: false
};

const mapStateToProps = state => ({
    loadingState: state.scratchGui.projectState.loadingState,
    projectChanged: state.scratchGui.projectChanged,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeFileMenuDispatch: () => dispatch(closeFileMenu()),
    openLoadingProjectDispatch: () => dispatch(openLoadingProject()),
    onLoadingFinishedDispatch: (loadingStateAfterUpload, success) => {
        dispatch(onLoadedProject(loadingStateAfterUpload, ownProps.canSave, success));
        dispatch(closeLoadingProject());
        dispatch(closeFileMenu());
    },
    setProjectTitleDispatch: title => dispatch(setProjectTitle(title)),
    requestProjectUploadDispatch: ls => dispatch(requestProjectUpload(ls))
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
    Object.assign({}, stateProps, dispatchProps, ownProps);

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(LabcodeMyProjectsModal));
